import React, { Fragment, useContext, useState } from "react";
import get from "lodash/get";
import styles from "./LoginPage.module.css";
import { ApolloClient, OperationVariables } from "@apollo/client";
import { useQuery, useLazyQuery, useMutation, useApolloClient } from "@apollo/client/react";
import { CombinedGraphQLErrors, ServerError } from "@apollo/client/errors";
import loginMutation from "./loginMutation";
import loginDelegateMutation from "./loginDelegateMutation";
import { Login, LoginVariables } from "./types/Login";
import { LoginDelegate, LoginDelegateVariables } from "./types/LoginDelegate";
import authService, { SET_AUTH } from "../../services/authService";
import { NavigateFunction, Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import { Box, TextField, Link, Button, Typography, Grid, Checkbox, Dialog, DialogContent, DialogActions } from "@mui/material";
import { Formik } from "formik";
import MainNavigation from "../MainNavigation/MainNavigation";
import AuthLayout from "../common/AuthLayout/AuthLayout";
import Announcement from "../Announcement/Announcement";
import { getQueryAnnouncement } from "../Announcement/types/getQueryAnnouncement";
import getAnnouncements from "../Announcement/getAnnouncement";
import AppContext from "../../context/AppContext";
import * as Constants from "../../constants";
import advancedSearch from "../../model/advanced-search";
import queryString from "query-string";
import { animateScroll } from "react-scroll";
import { useEffect } from "react";
import IconButton from "@mui/material/IconButton";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import Loading from "../Loading/Loading";
import { GetOptions } from "../App/types/GetOptions";
import GetOptionsQuery from "../App/GetOptionsQuery";
import { UpdateUserDeviceSettings, UpdateUserDeviceSettingsVariables } from "./types/UpdateUserDeviceSettings";
import UpdateUserDeviceSettingsMutation from "./UpdateUserDeviceSettingsMutation";
import { isTenantVet } from "../../services/tenantService";
import { storeMobileDeviceToken } from "./storeMobileDeviceToken";
import { getCpdProviders } from "./types/getCpdProviders";
import GetCpdProvidersQuery from "./GetCpdProvidersQuery";
import ACLLogo from "../../assets/CL logo.svg";
import SouthernSunLogo from "../../assets/SouthernSun Logo CMYK-GreyTag.svg";
import SpecialisedTrialsBlueLogo from "../../assets/Specialised Trials Blue CMYK Small.svg";
import gribblesVetLogo from "../../assets/gribbles-vet.svg";
import { getBiometricDiagnostics, BiometricDiagnostics } from "../../services/mobilityService";
import checkUserLoginTypeQuery from "./checkUserLoginTypeQuery";
import { CheckUserLoginType, CheckUserLoginTypeVariables } from "./types/CheckUserLoginType";
import useBiometric from "../MFAAuthentication/useBiometric";
import { FaceIdIcon } from "../../utils/faceIdIcon";
import FingerprintIcon from "@mui/icons-material/Fingerprint";
import theme from "../theme";
import packageInfo from "../../../package.json";

const createStyles = () => ({
  image: {
    maxWidth: "200px",
    height: "auto",
    alignSelf: "center",
  },
  clnicalLabImage: {
    maxWidth: "200px",
    height: "auto",
    alignSelf: "center",
    marginTop: "24px",
  },
  vetImage: {
    maxWidth: "200px",
    height: "auto",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginLeft: "auto",
    marginRight: "auto",
  },
  globalGrid: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "center",
    "@media (max-width: 600px)": {
      alignItems: "center",
    },
    "@media (max-width: 960px)": {
      alignItems: "center",
    },
    "@media (min-width: 960px)": {
      alignItems: "flex-start",
    },
  },
});

type LoginFormErrors = {
  username?: boolean;
  password?: boolean;
};

type LoginFormValues = {
  username: string;
  password: string;
};

type LoginBoxProps = {
  isLoading: boolean;
  onSubmit: (username: string, password: string) => Promise<void>;
  isDelegateLoading?: boolean;
  error?: any;
  isAnnouncementAvai?: boolean;
  announcedata?: getQueryAnnouncement;
  checkUserLoginType: (username: string) => Promise<{ isSso: boolean; cognitoDisabled: boolean; identityProvider: string | null; cognitoClientId: string | null; cognitoDomain: string | null } | null>;
  isCheckingUser?: boolean;
};

let rememberUsername = false;
let checkBoxChecked = false;

export const LoginBox: React.FC<LoginBoxProps> = (props) => {
  const classes = createStyles();
  const { error, isLoading } = props;

  // Step 1: username only, Step 2: password (normal user)
  const [step, setStep] = React.useState<"username" | "password">("username");
  const [usernameValue, setUsernameValue] = React.useState(authService.getRememberMe());

  // Hidden biometric-diagnostics gesture: 5 taps on the ACL logo opens a modal
  // listing the device's biometric authenticator classes and availability.
  const logoTapCountRef = React.useRef(0);
  const logoTapTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const [diagnosticsOpen, setDiagnosticsOpen] = React.useState(false);
  const [diagnostics, setDiagnostics] = React.useState<BiometricDiagnostics | null>(null);

  const handleLogoTap = async () => {
    logoTapCountRef.current += 1;
    // Reset the counter if taps aren't rapid enough (1.5s between taps).
    if (logoTapTimerRef.current) clearTimeout(logoTapTimerRef.current);
    logoTapTimerRef.current = setTimeout(() => {
      logoTapCountRef.current = 0;
    }, 1500);

    if (logoTapCountRef.current >= 5) {
      logoTapCountRef.current = 0;
      if (logoTapTimerRef.current) clearTimeout(logoTapTimerRef.current);
      const result = await getBiometricDiagnostics();
      setDiagnostics(result);
      setDiagnosticsOpen(true);
    }
  };

  const [values, setValues] = React.useState({
    password: "",
    showPassword: false,
  });

  const initialValues: LoginFormValues = {
    username: authService.getRememberMe(),
    password: "",
  };

  const isDesktop = useContext(AppContext).isDesktop;

  if (authService.getRememberMe() !== "") {
    checkBoxChecked = true;
  } else {
    checkBoxChecked = false;
  }

  const [remChecked, setRemChecked] = React.useState(checkBoxChecked);

  if (remChecked) {
    rememberUsername = true;
  }

  const validateUsernameStep = (values: LoginFormValues) => {
    const errors: LoginFormErrors = {};
    if (!values.username) {
      errors.username = true;
    }
    return errors;
  };

  const validatePasswordStep = (values: LoginFormValues) => {
    const errors: LoginFormErrors = {};
    if (!values.username) {
      errors.username = true;
    }
    if (!values.password) {
      errors.password = true;
    }
    return errors;
  };

  const handleContinue = async (values: LoginFormValues) => {
    const username = values.username.trim();
    setUsernameValue(username);

    const result = await props.checkUserLoginType(username);

    // SSO requires the hospital-level Cognito flag (isSso) to be true and the
    // per-user cognito_disabled flag to be false. If the user has
    // cognito_disabled = true, fall back to normal username/password login
    // even when isSso is true.
    if (result && result.isSso && !result.cognitoDisabled) {
      // Persist "Remember me" before redirecting to SSO
      if (rememberUsername) {
        authService.setRememberMe(username);
      } else {
        authService.clearRememberMe();
      }

      // SSO user - redirect to Cognito authorize URL with the identity provider
      const cognitoClientId = result.cognitoClientId || import.meta.env.VITE_USER_POOL_CLIENT_ID;
      const identityProvider = result.identityProvider || "";
      const cognitoDomain = result.cognitoDomain || import.meta.env.VITE_COGNITO_DOMAIN || "eresult-dev.auth.ap-southeast-2.amazoncognito.com";
      const redirectUri = encodeURIComponent(`${window.location.origin}/login-sso`);

      const authorizeUrl =
        `https://${cognitoDomain}/oauth2/authorize` +
        `?identity_provider=${encodeURIComponent(identityProvider)}` +
        `&redirect_uri=${redirectUri}` +
        `&response_type=CODE` +
        `&client_id=${cognitoClientId}` +
        `&scope=email+openid+profile`;

      window.location.href = authorizeUrl;
    } else {
      // Normal user - show password step
      setStep("password");
    }
  };

  const submitPassword = (values: LoginFormValues) => {
    authService.clearAll();
    props.onSubmit(values.username.trim(), values.password);
  };

  const handleBack = () => {
    setStep("username");
  };

  const selectRemember = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      rememberUsername = true;
    } else {
      rememberUsername = false;
    }
    setRemChecked(event.target.checked);
  };

  function scrollTop() {
    animateScroll.scrollToTop();
  }

  const handleClickShowPassword = () => {
    setValues({ ...values, showPassword: !values.showPassword });
  };

  const handleMouseDownPassword = (event: any) => {
    event.preventDefault();
  };

  const receiveMessage = (event: any): void => {
    if (event.data) {
      const eDataString = event.data + "";
      let eData: string[] = [];

      if (eDataString.includes("\t")) {
        eData = eDataString.split("\t");
      } else {
        eData = eDataString.split("~");
      }

      if (eData[0] === "eResultsMobileLogin") {
        props.onSubmit(eData[1], eData[2]);
      }
    }
  };

  useEffect(() => {
    window.addEventListener("message", receiveMessage, false);
    return () => window.removeEventListener("message", receiveMessage, false);
  }, []);

  return (
    <Grid container className={isDesktop ? styles.mainBox : styles.mainBoxM}>
      <Grid size={{ xs: 12, md: props.isAnnouncementAvai ? 6 : 12 }}>
        <AuthLayout title="Sign In" maxWidth={"490px"} isAnnouncementAvai={props.isAnnouncementAvai}>
          <Box mt={2}>
            {error && (() => {
                if (CombinedGraphQLErrors.is(error)) {
                  return error.errors.map((graphqlError, index) => {
                    if (graphqlError.message) {
                      return (
                        <Typography key={index} color="error">
                          {
                            "Invalid email or password. Please email "
                          }
                          <Link
                            href={`mailto:esupport@clinicallabs.com.au?subject=eResults%20Access%20Error:%20${graphqlError.message}`}
                            variant="body1"
                            color="primary"
                            align="center"
                          >
                            <span style={{ margin: "0px 5px" }}>technical support</span>
                          </Link>
                          {" with reference ID "} {graphqlError.message}
                        </Typography>
                      );
                    } else {
                      return (
                        <Typography
                          key={index}
                          color="error"
                        >{Constants.NO_DB_CONNECTION}</Typography>
                      );
                    }
                  });
                } else if (ServerError.is(error)) {
                  return <Typography color="error">{error.message}</Typography>;
                } else {
                  return <Typography color="error">{(error as any)?.message || 'An error occurred'}</Typography>;
                }
              })()}
          </Box>
          {props.isDelegateLoading ? (
            <Box mt={2} className={styles.footer}>
              <Typography>Authenticating single use token... Please wait</Typography>
              <Loading></Loading>
            </Box>
          ) : step === "username" ? (
            /* Step 1: Username/Email only */
            <Formik initialValues={initialValues} validate={validateUsernameStep} onSubmit={handleContinue}>
              {({ errors, handleChange, handleBlur, handleSubmit, touched }) => (
                <form onSubmit={handleSubmit}>
                  <Box mt={4}>
                    <TextField
                      id="username"
                      name="username"
                      label="Username/Email"
                      fullWidth
                      variant="outlined"
                      onChange={handleChange}
                      error={!!errors.username && touched.username}
                      onBlur={handleBlur}
                      defaultValue={initialValues["username"]}
                    ></TextField>
                  </Box>

                  <Box mt={1}>
                    <Checkbox
                      name="Remember"
                      color="primary"
                      disabled={false}
                      onChange={selectRemember}
                      checked={remChecked}
                    />
                    Remember my username/email
                  </Box>

                  <Box mt={2} display="flex" justifyContent="space-between" alignItems="center">
                    <Box flexGrow={1}>
                      {!isTenantVet() && (
                        <Link
                          color="primary"
                          href="https://www.clinicallabs.com.au/register/ehealth/"
                          target="_blank"
                          rel="noopener"
                        >
                          Create account
                        </Link>
                      )}
                      {!authService.isMobility() && isTenantVet() && (
                        <Link
                          color="primary"
                          href="https://eresults.gribbles.com.au/ios/"
                          target="_blank"
                          rel="noopener"
                        >
                          Create account
                        </Link>
                      )}
                    </Box>

                    <Button
                      type="submit"
                      color="primary"
                      variant="contained"
                      disabled={props.isCheckingUser}
                      className={styles.loginButton}
                      onClick={scrollTop}
                      loading={props.isCheckingUser}
                    >
                      <span>Continue</span>
                    </Button>
                  </Box>
                </form>
              )}
            </Formik>
          ) : (
            /* Step 2: Password (normal user) */
            <Formik
              initialValues={{ username: usernameValue, password: "" }}
              validate={validatePasswordStep}
              onSubmit={submitPassword}
            >
              {({ errors, handleChange, handleBlur, handleSubmit, touched }) => (
                <form onSubmit={handleSubmit}>
                  <Box mt={4}>
                    <TextField
                      id="username"
                      name="username"
                      label="Username/Email"
                      fullWidth
                      variant="outlined"
                      value={usernameValue}
                      disabled
                    ></TextField>
                  </Box>
                  <Box mt={2}>
                    <TextField
                      id="password"
                      name="password"
                      fullWidth
                      label="Password"
                      type={values.showPassword ? "text" : "password"}
                      variant="outlined"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      autoFocus
                      error={!!errors.password && touched.password}
                      slotProps={{
                        input: {
                          endAdornment: (
                            <IconButton
                              aria-label="toggle password visibility"
                              onClick={handleClickShowPassword}
                              onMouseDown={handleMouseDownPassword}
                              edge="end"
                            >
                              {values.showPassword ? <Visibility /> : <VisibilityOff />}
                            </IconButton>
                          ),
                        },
                      }}
                    ></TextField>
                  </Box>

                  <Box mt={3}>
                    <Link color="primary" component={RouterLink} to="/forgot">
                      Forgot password?
                    </Link>
                  </Box>

                  <Box mt={1}>
                    <Checkbox
                      name="Remember"
                      color="primary"
                      disabled={false}
                      onChange={selectRemember}
                      checked={remChecked}
                    />
                    Remember my username/email
                  </Box>

                  <Box mt={2} display="flex" justifyContent="space-between" alignItems="center">
                    <Button
                      color="primary"
                      variant="contained"
                      className={styles.loginButton}
                      onClick={handleBack}
                    >
                      Back
                    </Button>

                    <Button
                      type="submit"
                      color="primary"
                      variant="contained"
                      disabled={isLoading}
                      className={styles.loginButton}
                      onClick={scrollTop}
                      loading={isLoading}
                    >
                      <span>Login</span>
                    </Button>
                  </Box>
                </form>
              )}
            </Formik>
          )}
        </AuthLayout>
      </Grid>
      {props.isAnnouncementAvai && props.announcedata && (
        <Grid size={{ xs: 12, md: 6 }}>
          <Announcement data={props.announcedata} />
        </Grid>
      )}
      {!isTenantVet() && (
        <Grid size={{ xs: 12 }}>
          <Grid container display="flex" sx={classes.globalGrid} rowSpacing={4} style={{ marginTop: "10px" }}>
            <Grid size={{ xs: 12, sm: 12, md: 3 }} sx={classes.globalGrid}>
              <img src={ACLLogo} style={classes.clnicalLabImage} onClick={handleLogoTap} />
            </Grid>
            <Grid size={{ xs: 12, sm: 12, md: 3 }} sx={classes.globalGrid}>
              <img src={SpecialisedTrialsBlueLogo} style={classes.clnicalLabImage} />
            </Grid>
            <Grid size={{ xs: 12, sm: 12, md: 3 }} sx={classes.globalGrid}>
              <img src={SouthernSunLogo} style={classes.image} />
            </Grid>
          </Grid>
        </Grid>
      )}
      {isTenantVet() && (
        <Grid size={{ xs: 12 }}>
          <Grid container spacing={2} display="flex" alignItems="center" justifyContent="center" mt={4}>
            <Grid size={{ xs: 12, sm: 12, md: 3 }}>
              <img src={gribblesVetLogo} style={classes.vetImage} onClick={handleLogoTap} />
            </Grid>
          </Grid>
        </Grid>
      )}
      <Grid size={12}>
        <Box mt={4} className={styles.footer}>
          <Typography variant="body2" color="textSecondary">
            By logging in you agree to the{" "}
            <Link
              className={styles.terms}
              color="textSecondary"
              href="https://ehealth.clinicallabs.com.au/terms_conditions.php"
              target="_blank"
              rel="noopener"
            >
              Terms &amp; Conditions
            </Link>
          </Typography>
        </Box>
        <Box mt={1} mb={2} className={styles.footer}>
          <Typography variant="body2" color="textSecondary" className={styles.version} align={"center"}>
            eResults version {packageInfo.version}
          </Typography>
        </Box>
      </Grid>

      {/* Hidden biometric-diagnostics modal (opened by 5 taps on the ACL logo). */}
      <Dialog
        open={diagnosticsOpen}
        onClose={() => setDiagnosticsOpen(false)}
        maxWidth={"md"}
        aria-labelledby="biometric-diagnostics-dialog-title"
        sx={{ paper: { width: "380px" } }}
      >
        <DialogContent>
          <Typography variant="h4" sx={{ color: theme.palette.primary.main, marginBottom: "10px" }}>
            Biometric capabilities
          </Typography>
          {!diagnostics || diagnostics.capabilities.length === 0 ? (
            <Typography>No biometric capability information available on this device.</Typography>
          ) : (
            <>
              {diagnostics.platform && (
                <Typography variant="body2" color="textSecondary" sx={{ marginBottom: "8px" }}>
                  Platform: {diagnostics.platform}
                </Typography>
              )}
              {diagnostics.capabilities.map((cap, i) => (
                <Box key={i} mb={1}>
                  <Typography variant="body1">
                    {cap.name}
                    {cap.class ? ` — ${cap.class}` : ""}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {cap.available ? "✓ " : "✗ "}
                    {cap.status}
                  </Typography>
                </Box>
              ))}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button variant="contained" color="primary" onClick={() => setDiagnosticsOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
};

// TODO: shouldn't manually set route on successful login to /patients,
// ProtectedRoute saves the current route in location state which
// should be restored here and redirected to.

const onSubmit =
  (
    location: ReturnType<typeof useLocation>,
    history: NavigateFunction,
    login: useMutation.ResultTuple<Login, LoginVariables>[0],
    client: ApolloClient,
    siteOptions: useLazyQuery.ExecFunction<GetOptions, Record<string, never>>,
    updateUsersDeviceConfig: useMutation.ResultTuple<UpdateUserDeviceSettings, UpdateUserDeviceSettingsVariables>[0],
    cpdProviders: useLazyQuery.ExecFunction<getCpdProviders, OperationVariables>,
  ) =>
  async (username: string, password: string) => {
    // TODO: persist token to local storage
    const useragent = navigator.userAgent;
    const mobility = authService.isMobility();
    const setCookie = true;
    const trustBrowser = authService.getCookieValue(`trustBrowser_${encodeURIComponent(username.toUpperCase())}`);
    let deviceId = "";

    try {
      if (authService.isMobility()) {
        const devicevalue = authService.getMobileDeviceId();
        const device = devicevalue ? devicevalue.split("|") : [];

        if (device.length >= 4) {
          deviceId = device[0];
        }
      }

      const result = await login({
        variables: {
          input: {
            username,
            password,
            useragent,
            mobility,
            setCookie,
            trustBrowser,
            deviceId,
          },
        },
      });

      if (result.data) {
        authService.clearSearchText(); // http://jira.pl.pathlabs.com.au/browse/ER8-97
        authService.persistAllValues(result.data.login);
        authService.clearDelegatedUser(); //Clear delegated user in any case if we logged in via normal login.

        if (rememberUsername) {
          authService.setRememberMe(username);
        } else {
          authService.clearRememberMe();
        }

        client.cache.writeQuery({
          query: SET_AUTH,
          data: { auth: { isAuth: true, __typename: "Auth" } },
        });

        const isCtFrom = authService.isClinicalTrial() && !mobility ? "/patient-episodes" : "/patients";
        let from: any = get(location, "state.from") || isCtFrom;

        // Get site options
        await siteOptions({}).then((v) => {
          if (v.data) {
            authService.setSiteOptions(v.data.getOptions);
          }
        });
        // if (options.data) {
        //   authService.setSiteOptions(options.data.getOptions);
        // }

        //For the default ward search we are including advancedSearch query string here
        if (authService.getDefaultWardCode()) {
          const wards = authService.getWards();

          const AdSearch: advancedSearch = new advancedSearch();
          AdSearch.wardCode = wards.find((w) => w.code === authService.getDefaultWardCode());
          const newquerystring = `?${AdSearch.toQueryString()}`;
          // eslint-disable-next-line no-const-assign
          from = get(location, "state.from") || `/patients${newquerystring}`;
        }

        let otpRequired = false;
        if (
          authService.getMFAenrolled() == true &&
          authService.getOtpVerfiedEnrolled() == false &&
          authService.getForceAuth() == true
        ) {
          authService.setLastViewed(typeof from !== "string" && from.pathname ? from.pathname : from);
          from = "/mfaauthentication";
          otpRequired = true;
        } else if (authService.getForceEnrol()) {
          //Goes back to /enrolment
          from = "/enrolment";
          otpRequired = true;
        }

        if (!otpRequired) {
          await storeMobileDeviceToken(updateUsersDeviceConfig, cpdProviders);
        }

        await client.resetStore();
        history(from, { replace: true });
      }
    } catch (e) {
      // don't do anything - the error will be displayed on screen
      console.error(e);
    }
  };

// Module-scoped in-flight guard for the cold-launch biometric bypass. Kept
// outside the component so it survives LoginPage re-mounts (the native webview
// shell re-mounts this page when the OS biometric prompt changes Activity
// focus/visibility). Without this, each re-mount would re-fire the launch and
// loop the prompt. Reset when an attempt settles or on an explicit retry.
let coldLaunchInFlight = false;

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [login, { loading, error }] = useMutation<Login, LoginVariables>(loginMutation);
  const [loginDelegate, { loading: loadingDelegate, error: errorDelegate }] = useMutation<
    LoginDelegate,
    LoginDelegateVariables
  >(loginDelegateMutation);
  const [siteOptions, { loading: optionsLoading }] = useLazyQuery<GetOptions, Record<string, never>>(GetOptionsQuery);
  const [updateUsersDeviceConfig] = useMutation<UpdateUserDeviceSettings, UpdateUserDeviceSettingsVariables>(
    UpdateUserDeviceSettingsMutation,
  );
  const [cpdProviders] = useLazyQuery<getCpdProviders, OperationVariables>(GetCpdProvidersQuery);
  const [checkLoginType, { loading: checkingUser }] = useLazyQuery<CheckUserLoginType, CheckUserLoginTypeVariables>(
    checkUserLoginTypeQuery,
  );

  const qs = queryString.parse(location.search);
  const mobility = qs.ref === "mobility";
  if (mobility) {
    authService.setMobility(mobility);
  }

  const { data } = useQuery<getQueryAnnouncement>(getAnnouncements, {});
  const [isAnnouncementAvai, setIsAnnouncementAvai] = useState(false);
  useEffect(() => {
    if (data) {
      if (data.announcement.announce.length > 0) {
        setIsAnnouncementAvai(true);

        if (mobility) {
          const e = document.createElement("base");
          e.target = "_blank";
          document.head.appendChild(e);
        }
      }
    } else {
      setIsAnnouncementAvai(false);
    }
  }, [data, mobility]);

  const [runOnce, setRunOnce] = useState(false);

  const client: ApolloClient = useApolloClient();

  const singleUseToken = qs.singleusetoken || "";

  if (singleUseToken && typeof singleUseToken === "string" && !runOnce) {
    //   //Should logged in directly here.
    setRunOnce(true);

    const useragent = navigator.userAgent;
    const setCookie = true;

    try {
      loginDelegate({
        variables: {
          input: {
            singleUseToken,
            useragent,
            mobility,
            setCookie,
          },
        },
      })
        .then(async (result) => {
          if (!result.data) {
            return;
          }

          authService.clearSearchText(); // http://jira.pl.pathlabs.com.au/browse/ER8-97
          authService.persistAllValues(result.data.loginDelegate);
          authService.setDelegatedUser(true);

          //always set to non-expired if login via delegate.
          authService.clearExpired();

          client.cache.writeQuery({
            query: SET_AUTH,
            data: { auth: { isAuth: true, __typename: "Auth" } },
          });

          const isCtFrom = authService.isClinicalTrial() && !mobility ? "/patient-episodes" : "/patients";
          let from = get(location, "state.from") || isCtFrom;

          // Get site options
          const options = await siteOptions({});
          if (options.data) {
            authService.setSiteOptions(options.data.getOptions);
          }

          //For the default ward search we are including advancedSearch query string here
          if (authService.getDefaultWardCode()) {
            const wards = authService.getWards();

            const AdSearch: advancedSearch = new advancedSearch();
            AdSearch.wardCode = wards.find((w) => w.code === authService.getDefaultWardCode());
            const newquerystring = `?${AdSearch.toQueryString()}`;
            // eslint-disable-next-line no-const-assign
            from = get(location, "state.from") || `/patients${newquerystring}`;
          }

          await cpdProviders().then((v) => {
            if (v.data) {
              authService.setCpdProviders(v.data.getCpdProviders);
            }
          });

          // these navigate commands are required to make sure the "Back to patient list" option works
          // when "Open New tab" option is not enabled
          navigate(from, { replace: true });
          document.location.reload();
        })
        .catch(() => {
          // don't do anything - the error will be displayed on screen
        });
    } catch {
      // don't do anything - the error will be displayed on screen
    }
  }

  const handleCheckUserLoginType = async (username: string) => {
    try {
      const result = await checkLoginType({ variables: { username } });
      if (result.data?.checkUserLoginType) {
        return result.data.checkUserLoginType;
      }
      return null;
    } catch {
      // If the check fails, default to normal login flow
      return null;
    }
  };

  // --- Returning-user biometric launch bypass (mobile only) ---
  // On app launch inside the native shell, if this device has biometrics
  // enabled we immediately prompt Face ID / fingerprint and, on success, go
  // straight into eResults — bypassing the username/password and MFA screens.
  const biometric = useBiometric();
  const canBiometricLaunch = !singleUseToken && biometric.canVerify();
  // The biometric cold-launch AUTO-triggers on mount when a launch verification
  // is possible: we start behind the "Verifying..." screen (so the login form
  // doesn't flash) and fire the prompt from the mount effect below. On failure
  // we fall back to the biometric button screen (biometricFailed) where the user
  // can retry or switch to password.
  // When the user arrived here by signing out, we must NOT auto-trigger the
  // biometric cold-launch. Seed the state so we land directly on the biometric
  // button screen (no "Verifying..." flash) and skip the auto-launch below.
  const fromLogout = (location.state as { fromLogout?: boolean } | null)?.fromLogout === true;
  const [biometricInProgress, setBiometricInProgress] = useState(canBiometricLaunch && !fromLogout);
  const [biometricFailed, setBiometricFailed] = useState(canBiometricLaunch && fromLogout);
  // Whether a biometric verification just FAILED. Briefly swaps the verifying
  // screen's text to an error message before falling back to the button screen.
  const [biometricError, setBiometricError] = useState(false);

  const runBiometricLaunch = async () => {
    // Guard against re-entry. Inside the native webview shell, showing the OS
    // biometric prompt drives the host Activity through inactive/paused/resumed
    // transitions, which can re-mount this page and re-run the launch effect.
    // Without a guard that survives re-mounts, each re-mount would issue a fresh
    // challenge and a new prompt before the in-flight sign completes — an
    // infinite re-prompt loop. `coldLaunchInFlight` is module-scoped so it
    // persists across re-mounts; it is cleared when the attempt settles.
    if (coldLaunchInFlight) return;
    coldLaunchInFlight = true;

    // A launch/retry attempt re-enables the fast-path for this session.
    biometric.clearSuppressForSession();
    setBiometricError(false);
    setBiometricInProgress(true);
    // verify(destination, launch=true) runs the unauthenticated cold-launch
    // login path (resolves the user from the device alone). It navigates into
    // the app on success. On failure we briefly show a "Couldn't verify your
    // identity" message on the verifying screen, then fall back to the biometric
    // button screen.
    let ok = false;
    try {
      ok = await biometric.verify(undefined, true);
      if (!ok) {
        // Keep the verifying screen up and swap its text to the error for a
        // brief moment, then drop to the button screen.
        setBiometricError(true);
        setTimeout(() => {
          setBiometricError(false);
          setBiometricInProgress(false);
          setBiometricFailed(true);
        }, 2000);
      }
    } finally {
      // On success we are navigating into the app; keep the guard set so a
      // re-mount during the navigation transition cannot re-fire the prompt.
      // On failure, release it so "Try Again" (or a later attempt) can run.
      if (!ok) {
        coldLaunchInFlight = false;
      }
    }
  };

  // Auto-login: if cognito tokens exist from a previous session, use refresh token to re-authenticate
  const [autoLoginAttempted, setAutoLoginAttempted] = useState(false);
  const [autoLoginInProgress, setAutoLoginInProgress] = useState(() => {
    // Start in loading state if we have persisted tokens to avoid flash of login form
    return !singleUseToken && !!authService.getPersistedCognitoTokens();
  });

  useEffect(() => {
    const attemptAutoLogin = async () => {
      // Cognito "Remember me" auto-login (web/SSO).
      // Check localStorage for persisted cognito tokens (saved when "Remember me" was checked)
      const persistedTokens = authService.getPersistedCognitoTokens();
      if (!persistedTokens || !persistedTokens.refreshToken || autoLoginAttempted) {
        setAutoLoginInProgress(false);
        return;
      }

      setAutoLoginAttempted(true);

      // Redirect to /login-sso which handles the full auto-login flow
      // (restore tokens, refetch, load site options + CPD, navigate to app)
      window.location.href = "/login-sso" + (mobility ? "?ref=mobility" : "");
    };

    if (!singleUseToken) {
      attemptAutoLogin();
    } else {
      setAutoLoginInProgress(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // On mount: reset the cold-launch in-flight guard (a logout returns the user
  // to a freshly-mounted LoginPage, and a guard left set by a prior successful
  // launch — intentionally kept set through the success -> navigate transition —
  // would otherwise persist across the logout and block the next launch), then
  // auto-trigger the biometric cold-launch when it's available. refreshMethodName
  // also resolves the device's label ("Face ID"/"Fingerprint") for the UI.
  useEffect(() => {
    coldLaunchInFlight = false;
    if (canBiometricLaunch) {
      biometric.refreshMethodName();
      // When the user arrived here by signing out, do NOT auto-trigger the
      // biometric prompt. The initial state already shows the biometric button
      // screen so they can explicitly choose biometrics or another method.
      if (!fromLogout) {
        runBiometricLaunch();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const trailingText = biometric.methodName.toLowerCase().includes("fingerprint") ? " Unlock" : "";

  // TODO: should use useCallback for onSubmit cb
  return (
    <Fragment>
      <MainNavigation hideTopMenu isAnnouncementAvai={isAnnouncementAvai} />
      {biometricInProgress ? (
        <AuthLayout title="" maxWidth={"490px"} isAnnouncementAvai={false} doNotShow={true}>
          <Box mt={15} display="flex" flexDirection="column" justifyContent="center" alignItems="center">
            <Typography>Verifying with {biometric.methodName} {trailingText}...</Typography>
            <Loading />
            {biometricError && (
              <Typography color="error" align="center">
                Couldn&apos;t verify your identity
              </Typography>
            )}
          </Box>
        </AuthLayout>
      ) : biometricFailed ? (
        <AuthLayout title="" maxWidth={"490px"} isAnnouncementAvai={false} doNotShow={true}>
          <Box mt={15} display="flex" flexDirection="column" justifyContent="center" alignItems="center">
            <Box mt={3} display="flex" flexDirection="column" gap={2} width="100%">
              <Button
                variant="outlined"
                color="primary"
                onClick={runBiometricLaunch}
                fullWidth
                sx={{ borderRadius: "999px", padding: "10px" }}
              >
                {biometric.methodName === "Face ID" || biometric.methodName === "Face Unlock" ? <FaceIdIcon /> : <FingerprintIcon />} &nbsp;&nbsp;&nbsp;Login with {biometric.methodName} {trailingText}
              </Button>
              <Button
                variant="contained"
                color="primary"
                fullWidth
                sx={{ borderRadius: "999px", padding: "10px" }}
                onClick={() => {
                  // Fall back to the standard username/password login form. Do
                  // NOT suppress the MFA-page biometric skip: for an
                  // enrolled-but-not-yet-active device the cold-launch path
                  // legitimately fails, but the authenticated MFA-page verify is
                  // what promotes the key to active and must still run.
                  setBiometricFailed(false);
                }}
              >
                Use another method
              </Button>
            </Box>
          </Box>
        </AuthLayout>
      ) : autoLoginInProgress ? (
        <AuthLayout title="Sign In" maxWidth={"490px"} isAnnouncementAvai={false}>
          <Box mt={2} display="flex" flexDirection="column" justifyContent="center" alignItems="center">
            <Typography>Signing you in... Please wait</Typography>
            <Loading />
          </Box>
        </AuthLayout>
      ) : (
        <LoginBox
          isDelegateLoading={loadingDelegate}
          isLoading={loading || optionsLoading}
          onSubmit={onSubmit(location, navigate, login, client, siteOptions, updateUsersDeviceConfig, cpdProviders)}
          error={error || errorDelegate}
          isAnnouncementAvai={isAnnouncementAvai}
          announcedata={data}
          checkUserLoginType={handleCheckUserLoginType}
          isCheckingUser={checkingUser}
        />
      )}
    </Fragment>
  );
};

export default LoginPage;
